<template>
  <div class="relative">
    <q-inner-loading :showing="isLoading">
      <q-spinner-gears size="100px" color="primary" />
    </q-inner-loading>

    <q-banner
      v-if="error"
      inline-actions
      rounded
      class="bg-gradient-to-r from-orange-500 to-red-500 text-white m-4 rounded-xl"
    >
      <q-icon name="error" color="white" size="2em" />
      <span class="ml-2 font-semibold">{{ error }}</span>
    </q-banner>

    <div class="p-6 space-y-6">
      <q-banner
        v-if="!isNative"
        class="bg-orange-100 text-orange-800 border border-orange-200 rounded-lg"
        rounded
        inline-actions
      >
        <div class="flex items-center gap-2">
          <q-icon name="warning" color="orange-7" />
          <span>当前为 Web 环境，原生能力不可用</span>
        </div>
      </q-banner>

      <div
        class="bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-600"
      >
        <div class="flex items-center gap-3 mb-4">
          <div
            class="w-10 h-10 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center"
          >
            <q-icon name="explore" class="text-white text-xl" />
          </div>
          <div>
            <h2 class="text-2xl font-bold text-gray-800 dark:text-gray-100">
              预瞄 SOA 信号探索
            </h2>
            <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
              信号清单见
              <code class="text-xs">docs/previewinfo_soa.md</code>
              ；全名与
              <code class="text-xs">VehicleProperty.java</code>
              中
              <code class="text-xs">SusPreviewInfo_*</code>
              一致。数组型仅列表展示，不提供曲线图。
            </p>
          </div>
        </div>

        <div class="space-y-4">
          <p class="text-sm text-gray-600 dark:text-gray-400">
            共 {{ subscribeSignals.length }} 项可订阅（均为 SusPreviewInfo / PCM 预瞄相关）。
          </p>
          <div class="flex gap-3">
            <q-select
              v-model="selectedSignals"
              :options="signalOptions"
              label="选择信号"
              multiple
              emit-value
              map-options
              outlined
              dense
              use-chips
              class="flex-1"
              bg-color="white"
            />
            <button
              type="button"
              @click="toggleSelectAll"
              class="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg text-sm whitespace-nowrap"
            >
              {{
                selectedSignals.length === allSelectableSignalValues.length
                  ? "取消全选"
                  : "全选"
              }}
            </button>
          </div>

          <div class="flex gap-3">
            <button
              type="button"
              @click="handleGet"
              :disabled="!selectedSignals.length || !isNative"
              class="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <div class="flex items-center justify-center gap-2">
                <q-icon name="download" />
                <span>Get 获取</span>
              </div>
            </button>
            <button
              v-if="!isSubscribed"
              type="button"
              @click="handleSubscribe"
              :disabled="!selectedSignals.length"
              class="flex-1 px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <div class="flex items-center justify-center gap-2">
                <q-icon name="notifications_active" />
                <span>订阅</span>
              </div>
            </button>
            <button
              v-else
              type="button"
              :disabled="!selectedSignals.length"
              @click="handleUnsubscribe"
              class="flex-1 px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <div class="flex items-center justify-center gap-2">
                <q-icon name="notifications_off" />
                <span>取消订阅</span>
              </div>
            </button>
          </div>
        </div>
      </div>

      <div
        class="bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-600"
      >
        <div class="flex items-center gap-3 mb-4">
          <div
            class="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center"
          >
            <q-icon name="data_usage" class="text-white text-xl" />
          </div>
          <h2 class="text-2xl font-bold text-gray-800 dark:text-gray-100">
            最新数据
          </h2>
          <span
            v-if="Object.keys(displayData).length > 0"
            class="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full text-sm font-bold"
          >
            {{ Object.keys(displayData).length }}
          </span>
        </div>

        <div
          v-if="Object.keys(displayData).length > 0"
          class="grid grid-cols-1 gap-3 max-h-[560px] overflow-y-auto pr-2"
        >
          <div
            v-for="(data, propertyName) in displayData"
            :key="propertyName"
            class="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl p-4 border border-blue-200 dark:border-blue-700/50"
          >
            <div
              class="font-bold text-gray-800 dark:text-gray-200 text-sm tracking-wide mb-1"
            >
              {{ getSignalLabel(String(propertyName)) }}
            </div>
            <div class="text-xs text-gray-500 dark:text-gray-400 mb-2">
              {{ typeLabelForProperty(String(propertyName)) }}
            </div>
            <div
              v-if="Array.isArray(data.value)"
              class="text-sm font-mono text-blue-800 dark:text-blue-200 bg-white/60 dark:bg-gray-900/40 rounded-lg p-3 max-h-40 overflow-auto border border-blue-100 dark:border-blue-800"
            >
              <span class="text-gray-500 dark:text-gray-400"
                >[{{ arrayLength(data.value) }}] </span
              >{{ formatArrayValue(data.value) }}
            </div>
            <div
              v-else
              class="text-2xl font-bold text-blue-700 dark:text-blue-300 font-mono mb-2 break-all"
            >
              {{ formatScalarValue(data.value) }}
            </div>
            <div
              class="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mt-2"
            >
              <span>
                {{
                  data.timestamp
                    ? new Date(data.timestamp).toLocaleTimeString()
                    : "--"
                }}
              </span>
              <span
                :class="
                  data.status === 'OK'
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-red-600 dark:text-red-400'
                "
                class="font-semibold"
              >
                {{ data.status || "--" }}
              </span>
            </div>
          </div>
        </div>

        <div v-else class="text-center py-12 text-gray-500 dark:text-gray-400">
          <q-icon name="inbox" size="64px" class="mb-4 opacity-50" />
          <p class="text-lg">暂无数据</p>
          <p class="text-sm mt-2">选择信号后点击 Get 或 订阅 获取数据</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onUnmounted } from "vue";
import { useQuasar } from "quasar";
import CarPropertyClientClient, {
  CarPropertyClientValue,
  SubscribeOptionFlags,
} from "@/utils/capacitor/carPropertyClientPlugin";
import { isNative } from "@/utils/capacitor";
import type { SignalItem } from "@/composables/useCarProperty";
import {
  PREVIEWINFO_SOA_ROWS,
  toPreviewSoaSignalName,
  type PreviewSoaType,
} from "@/services/domainControl/previewinfoSoaCatalog";

const $q = useQuasar();

const subscribeSignals: SignalItem[] = PREVIEWINFO_SOA_ROWS.map((row) => ({
  signalName: toPreviewSoaSignalName(row.displayName),
  displayName: row.displayName,
}));

const metaBySignalLower = new Map<
  string,
  { zh: string; type: PreviewSoaType }
>();
for (const row of PREVIEWINFO_SOA_ROWS) {
  metaBySignalLower.set(toPreviewSoaSignalName(row.displayName).toLowerCase(), {
    zh: row.zh,
    type: row.type,
  });
}

const catalogByLower = new Map<string, SignalItem>();
for (const s of subscribeSignals) {
  catalogByLower.set(s.signalName.toLowerCase(), s);
}

const isLoading = ref(false);
const error = ref<string | null>(null);
const selectedSignals = ref<string[]>([]);
const displayData = ref<Record<string, CarPropertyClientValue>>({});
const isSubscribed = ref(false);
const subscribedPropertyNames = ref<string[]>([]);

const unsubscribeFns: (() => Promise<void>)[] = [];

const signalOptions = computed(() =>
  subscribeSignals.map((s) => {
    const m = metaBySignalLower.get(s.signalName.toLowerCase());
    const zh = m?.zh ?? s.displayName;
    return {
      label: `${zh} · ${s.displayName}`,
      value: s.signalName,
    };
  })
);

const allSelectableSignalValues = computed(() =>
  signalOptions.value.map((o) => o.value)
);

const toggleSelectAll = () => {
  if (
    selectedSignals.value.length === allSelectableSignalValues.value.length &&
    allSelectableSignalValues.value.length > 0
  ) {
    selectedSignals.value = [];
  } else {
    selectedSignals.value = [...allSelectableSignalValues.value];
  }
};

const getSignalLabel = (propertyName: string): string => {
  const item = catalogByLower.get(propertyName.toLowerCase());
  const meta = metaBySignalLower.get(propertyName.toLowerCase());
  if (item && meta) {
    return `${meta.zh} (${item.displayName})`;
  }
  return propertyName;
};

const typeLabelForProperty = (propertyName: string): string => {
  const meta = metaBySignalLower.get(propertyName.toLowerCase());
  return meta ? `类型: ${meta.type}` : "";
};

const formatScalarValue = (value: unknown): string => {
  if (value === null || value === undefined) return "--";
  if (typeof value === "number") {
    return value % 1 !== 0 ? value.toFixed(6) : value.toString();
  }
  if (typeof value === "bigint") return value.toString();
  return String(value);
};

const formatArrayValue = (value: unknown): string => {
  if (!Array.isArray(value)) return formatScalarValue(value);
  return value.map((v) => formatScalarValue(v)).join(", ");
};

const arrayLength = (value: unknown): number =>
  Array.isArray(value) ? value.length : 0;

const unsubscribeAll = async () => {
  for (const fn of unsubscribeFns) {
    try {
      await fn();
    } catch (e) {
      console.error("[SignalExplorer] unsubscribe 失败", e);
    }
  }
  unsubscribeFns.length = 0;
  isSubscribed.value = false;
  subscribedPropertyNames.value = [];
};

const initializeSS4 = async (propertyNames: string[]) => {
  if (propertyNames.length === 0) return;
  try {
    const result = await CarPropertyClientClient.getProperty({ propertyNames });
    const props: CarPropertyClientValue[] =
      "properties" in result
        ? result.properties
        : [result as CarPropertyClientValue];
    const nextDisplay: Record<string, CarPropertyClientValue> = {
      ...displayData.value,
    };
    for (const p of props) {
      if (p.propertyName) {
        nextDisplay[p.propertyName] = p;
      }
    }
    displayData.value = nextDisplay;
  } catch (e) {
    console.error("[SignalExplorer] getProperty 初始化失败", e);
  }
};

const subscribeSS4 = async (propertyNames: string[]) => {
  if (propertyNames.length === 0) return;
  await CarPropertyClientClient.subscribeProperty(
    {
      propertyNames,
      updateRateMs: 50,
      eventFlags:
        SubscribeOptionFlags.UPDATE | SubscribeOptionFlags.NEED_CURRENT_VALUE,
    },
    (data: CarPropertyClientValue) => {
      if (!data.propertyName) return;
      displayData.value = {
        ...displayData.value,
        [data.propertyName]: data,
      };
    }
  );
  unsubscribeFns.push(async () => {
    await CarPropertyClientClient.unsubscribeProperty({ propertyNames });
  });
};

const handleGet = async () => {
  if (!isNative || !selectedSignals.value.length) {
    $q.notify({
      type: "warning",
      message: "请选择信号，且此功能仅在原生应用中可用",
      position: "top",
    });
    return;
  }

  isLoading.value = true;
  error.value = null;

  try {
    const result = await CarPropertyClientClient.getProperty({
      propertyNames: selectedSignals.value,
    });

    const props: CarPropertyClientValue[] =
      "properties" in result
        ? result.properties
        : [result as CarPropertyClientValue];

    const next: Record<string, CarPropertyClientValue> = {
      ...displayData.value,
    };
    for (const p of props) {
      if (p.propertyName) next[p.propertyName] = p;
    }
    displayData.value = next;

    $q.notify({
      type: "positive",
      message: `成功获取 ${props.length} 个信号`,
      position: "top",
    });
  } catch (err) {
    error.value = (err as Error).message;
    $q.notify({
      type: "negative",
      message: "获取失败: " + (err as Error).message,
      position: "top",
    });
  } finally {
    isLoading.value = false;
  }
};

const handleSubscribe = async () => {
  isLoading.value = true;
  error.value = null;

  const names = [...selectedSignals.value];

  try {
    await unsubscribeAll();

    subscribedPropertyNames.value = names;

    try {
      await initializeSS4(names);
      await subscribeSS4(names);
    } catch (subErr) {
      console.error(
        "[SignalExplorer] initializeSS4 或 subscribeSS4 失败",
        subErr
      );
    }

    isSubscribed.value = true;
    $q.notify({
      type: "positive",
      message: `已订阅 ${names.length} 个信号`,
      position: "top",
    });
  } catch (err) {
    error.value = (err as Error).message;
    await unsubscribeAll();
    $q.notify({
      type: "negative",
      message: "订阅失败: " + (err as Error).message,
      position: "top",
    });
  } finally {
    isLoading.value = false;
  }
};

const handleUnsubscribe = async () => {
  isLoading.value = true;
  error.value = null;

  try {
    await unsubscribeAll();
    $q.notify({
      type: "positive",
      message: "已取消订阅",
      position: "top",
    });
  } catch (err) {
    error.value = (err as Error).message;
    $q.notify({
      type: "negative",
      message: "取消订阅失败: " + (err as Error).message,
      position: "top",
    });
  } finally {
    isLoading.value = false;
  }
};

onUnmounted(async () => {
  await unsubscribeAll();
});
</script>

<style scoped>
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 10px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 10px;
}

.dark .overflow-y-auto::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
}

.dark .overflow-y-auto::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
}
</style>
